import { createSlice } from "@reduxjs/toolkit";
import { InitialNotifications } from "../domain/models/Notification";
import { NotificationServiceImpl } from "../services/NotificationServiceImpl";
import { AppState } from "../store";
import _ from "lodash";
import { TFunction } from "i18next";
import { NotificationDTO } from "../domain/models/dto/NotificationDTO";
import { getCreatedTime } from "../tools/DatetimeTools";
import { DEFAULT_PAGE } from "../domain/constants";
import { ReviewServiceImpl } from "../services/ReviewServiceImpl";

export const notificationsSlice = createSlice({
    name: "notifications",
    initialState: InitialNotifications,
    reducers: {
        setReadCollectionPage: (state, page) => {
            state.readCollection.page = page.payload;
        },
        setUnReadCollectionPage: (state, page) => {
            state.unreadCollection.page = page.payload;
        },
    },
    extraReducers: (builder) => {
        builder.addCase(
            NotificationServiceImpl.getNotificationsThunk.fulfilled,
            (state, action) => {
                if (action.meta.arg.read) {
                    if (action.meta.arg.page === DEFAULT_PAGE) {
                        state.readCollection.rows = action.payload.rows;
                    } else {
                        state.readCollection.rows = _.uniqBy(
                            state.readCollection.rows.concat(
                                action.payload.rows
                            ),
                            "id"
                        );
                    }
                    state.readCollection.totalPages = action.payload.totalPages;
                    state.readCollection.totalRows = action.payload.totalRows;
                } else {
                    if (action.meta.arg.page === DEFAULT_PAGE) {
                        state.unreadCollection.rows = action.payload.rows;
                    } else {
                        state.unreadCollection.rows = _.uniqBy(
                            state.unreadCollection.rows.concat(
                                action.payload.rows
                            ),
                            "id"
                        );
                    }
                    state.unreadCollection.totalPages =
                        action.payload.totalPages;
                    state.unreadCollection.totalRows = action.payload.totalRows;
                }
            }
        );

        builder.addCase(
            NotificationServiceImpl.getShortNotificationsListThunk.fulfilled,
            (state, action) => {
                state.shortCollection.rows = action.payload.rows;
                state.shortCollection.totalPages = action.payload.totalPages;
                state.shortCollection.totalRows = action.payload.totalRows;
                state.shortCollection.meta = action.payload.meta;
            }
        );

        builder.addCase(
            NotificationServiceImpl.markAsReadNotificationThunk.fulfilled,
            (state, action) => {
                if (action.meta.arg.read) {
                    return;
                }

                state.shortCollection.rows = state.shortCollection.rows.filter(
                    (item) => item.id !== action.payload.id
                );
                state.shortCollection.meta.unreadCount -= 1;
            }
        );

        // handle post /review
        builder.addCase(
            ReviewServiceImpl.createReviewThunk.fulfilled,
            (state, action) => {
                const findWorkCallback = (item: NotificationDTO) =>
                    _.get(item, "application.work.id", "") ===
                    action.payload.workId;
                const notification =
                    state.readCollection.rows.find(findWorkCallback) ||
                    state.unreadCollection.rows.find(findWorkCallback);

                if (!_.get(notification, "application.work.reviews")) {
                    return;
                }
                notification.application.work.reviews.push(action.payload);
            }
        );
    },
});

export const selectReadNotifications = (state: AppState) =>
    state.notifications.readCollection;

export const selectUnreadNotifications = (state: AppState) =>
    state.notifications.unreadCollection;

export const selectShortNotificationsList = (state: AppState) =>
    state.notifications.shortCollection;

export const getNotificationCreatedTime = (
    t: TFunction,
    notification: NotificationDTO,
    useShortPrefix = false
) => {
    return getCreatedTime(
        t,
        notification.createdAt,
        useShortPrefix ? "short" : "default"
    );
};

export const { setReadCollectionPage, setUnReadCollectionPage } =
    notificationsSlice.actions;

export default notificationsSlice.reducer;
